import emaLib from 'exponential-moving-average'

const r4 = v => (Number.isNaN(v) ? NaN : Math.round(v * 1e4) / 1e4)
const arr4 = arr => arr.map(r4)

function applyBinop(op, a, b) {
  const arrA = Array.isArray(a)
  const arrB = Array.isArray(b)

  if (arrA && arrB) {
    switch (op) {
      case '+': return arr4(a.map((v, i) => v + b[i]))
      case '-': return arr4(a.map((v, i) => v - b[i]))
      case '*': return arr4(a.map((v, i) => v * b[i]))
      case '/': return arr4(a.map((v, i) => v / b[i]))
      default: throw new Error(`Unknown operator: ${op}`)
    }
  }
  if (arrA) {
    switch (op) {
      case '+': return arr4(a.map(v => v + b))
      case '-': return arr4(a.map(v => v - b))
      case '*': return arr4(a.map(v => v * b))
      case '/': return arr4(a.map(v => v / b))
      default: throw new Error(`Unknown operator: ${op}`)
    }
  }
  if (arrB) {
    switch (op) {
      case '+': return arr4(b.map(v => a + v))
      case '-': return arr4(b.map(v => a - v))
      case '*': return arr4(b.map(v => a * v))
      case '/': return arr4(b.map(v => a / v))
      default: throw new Error(`Unknown operator: ${op}`)
    }
  }
  switch (op) {
    case '+': return r4(a + b)
    case '-': return r4(a - b)
    case '*': return r4(a * b)
    case '/': return r4(a / b)
    default: throw new Error(`Unknown operator: ${op}`)
  }
}

// Pads EMA output at the front with NaN so the result length matches the input.
// Skips leading NaN values so chained EMAs (EMA of EMA) work correctly —
// emaLib propagates NaN forever if the input starts with NaN.
function ema(series, period) {
  const start = series.findIndex(v => !Number.isNaN(v))
  if (start === -1) return series.map(() => NaN)

  const result = emaLib(series.slice(start), period)
  const padding = new Array(start + period - 1).fill(NaN)

  return arr4([...padding, ...result])
}

// Shifts series right by one, filling the first element with defaultVal
function prev(series, defaultVal = 0) {
  return arr4([defaultVal, ...series.slice(0, -1)])
}

function min(a, b) {
  return arr4(a.map((v, i) => Math.min(v, b[i])))
}

function max(a, b) {
  return arr4(a.map((v, i) => Math.max(v, b[i])))
}

function nanToZero(series) {
  return arr4(series.map(v => (Number.isNaN(v) ? 0 : v)))
}

function abs(series) {
  return arr4(series.map(v => Math.abs(v)))
}

// Rolling highest value over `period` bars (equivalent to HHV in AmiBroker)
function hhv(series, period) {
  return arr4(series.map((_, i) => {
    const start = Math.max(0, i - period + 1)

    return Math.max(...series.slice(start, i + 1))
  }))
}

// Rolling lowest value over `period` bars (equivalent to LLV in AmiBroker)
function llv(series, period) {
  return arr4(series.map((_, i) => {
    const start = Math.max(0, i - period + 1)

    return Math.min(...series.slice(start, i + 1))
  }))
}

// Rolling simple moving average over `period` bars (equivalent to MA in AmiBroker).
// Returns NaN for any window containing NaN, matching hhv/llv behavior.
function sma(series, period) {
  return arr4(series.map((_, i) => {
    if (i < period - 1) return NaN
    let sum = 0
    for (let j = i - period + 1; j <= i; j += 1) {
      const v = series[j]
      if (Number.isNaN(v)) return NaN
      sum += v
    }

    return sum / period
  }))
}

// Linear regression slope over `period` bars (equivalent to LinRegSlope in AmiBroker)
function linRegSlope(series, period) {
  const n = period
  const sumX = (n * (n - 1)) / 2
  const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6
  const denom = n * sumX2 - sumX * sumX

  return arr4(series.map((_, i) => {
    if (i < n - 1) return NaN
    const window = series.slice(i - n + 1, i + 1)
    const sumY = window.reduce((a, v) => a + v, 0)
    const sumXY = window.reduce((a, v, j) => a + j * v, 0)

    return (n * sumXY - sumX * sumY) / denom
  }))
}

export {
  applyBinop, ema, prev, min, max, nanToZero, abs, hhv, llv, sma, linRegSlope,
}
